import api from 'app/api/axios-api';
import { Project, Uploads } from '@app/shared/types/project.type';

export interface CSAPlusProjectRaw {
  projectId: string;
  projectTitle?: string;
  title?: string;
  name?: string;
  description?: string;
  domainName?: string;
  userId?: string;
  uploads?: unknown[];
  created_at?: string;
  updated_at?: string;
}

export interface CSAPlusAllProjectsResponse {
  status: number;
  count: number;
  projects: CSAPlusProjectRaw[];
}

function mapToProject(raw: CSAPlusProjectRaw): Project {
  const title = raw.projectTitle ?? raw.title ?? raw.name ?? '';
  return {
    projectId: raw.projectId,
    title,
    overview: raw.description ?? '',
    domain: (raw.domainName as any) ?? '',
    userId: raw.userId ?? '',
    organizationId: localStorage.getItem('organizationId') ?? '',
    uploads: raw.uploads as Uploads | undefined,
    createdOn: raw.created_at ?? raw.updated_at
  };
}

export async function fetchCSAPlusProjects(params: { userId: string; excludeUploads?: boolean }): Promise<Project[]> {
  const { userId, excludeUploads } = params;
  if (!userId) {
    throw new Error('Missing required query parameter: userId');
  }
  const query = new URLSearchParams({ userId });
  if (excludeUploads) query.set('excludeUploads', 'true');
  const queryString = query.toString();
  const url = `/csa-plus/all-projects?${queryString}`;
  const response = await api.get<CSAPlusAllProjectsResponse>(url);
  const data = response.data;
  const projects = data?.projects ?? [];
  return projects.map(mapToProject);
}

export async function fetchCSAPlusProjectById(projectId: string, userId: string): Promise<Project | null> {
  const projects = await fetchCSAPlusProjects({ userId, excludeUploads: false });
  return projects.find(p => p.projectId === projectId) ?? null;
}

export interface CSAPlusUploadItem {
  upload_id: string;
  original_filename: string;
  file_url: string;
  blob_path: string;
  doc_type: string;
  uploaded_at: string;
  status: string;
  chroma_url?: string;
  chroma_blob_path?: string;
  rules_extracted?: number;
}

export interface CSAPlusProjectResponse {
  status: number;
  project: {
    projectId: string;
    projectTitle?: string;
    description?: string;
    domainName?: string;
    userId?: string;
    uploads?: CSAPlusUploadItem[] | Record<string, CSAPlusUploadItem | null>;
    created_at?: string;
    updated_at?: string;
  };
}

export async function fetchCSAPlusProject(projectId: string): Promise<CSAPlusProjectResponse['project'] | null> {
  const response = await api.get<CSAPlusProjectResponse>(`/csa-plus/project/${encodeURIComponent(projectId)}`);
  const data = response.data;
  if (data?.status === 404 || !data?.project) return null;
  return data.project ?? null;
}

/** `uploads` may be an array or a map; map values can be null. */
export function listCSAPlusUploads(
  uploads: CSAPlusProjectResponse['project']['uploads'] | undefined | null
): CSAPlusUploadItem[] {
  if (!uploads) return [];
  if (Array.isArray(uploads)) return uploads.filter((u): u is CSAPlusUploadItem => u != null);
  return Object.values(uploads).filter((u): u is CSAPlusUploadItem => u != null);
}

export interface UploadPolicyParams {
  projectId: string;
  file: File;
  docType?: string;
  convertToVector?: boolean;
}

export async function uploadPolicyDocument(params: UploadPolicyParams): Promise<unknown> {
  const { projectId, file, docType = 'policy', convertToVector = true } = params;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('doc_type', docType);
  formData.append('convert_to_vector', String(convertToVector));
  const response = await api.post(`/csa-plus/${encodeURIComponent(projectId)}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

export interface UploadConfigParams {
  projectId: string;
  file: File;
}

export async function uploadConfigDocument(params: UploadConfigParams): Promise<unknown> {
  const { projectId, file } = params;
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/csa-plus/${encodeURIComponent(projectId)}/config-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

export interface UpdateCSAPlusProjectParams {
  projectId: string;
  projectTitle?: string;
  description?: string;
  domainName?: string;
  userId?: string;
}

export async function updateCSAPlusProject(params: UpdateCSAPlusProjectParams): Promise<unknown> {
  const { projectId, projectTitle, description, domainName, userId } = params;
  const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const url = `/csa-plus/project/${encodeURIComponent(projectId)}${query}`;
  const body: Record<string, string> = {};
  if (projectTitle !== undefined) body.projectTitle = projectTitle;
  if (description !== undefined) body.description = description;
  if (domainName !== undefined) body.domainName = domainName;
  const response = await api.put(url, Object.keys(body).length ? body : {});
  return response.data;
}

export interface LogUploadResponse {
  upload_id: string;
  file_url: string;
  total_events: number;
  canonical_events: unknown[];
}

export async function uploadLogFile(params: {
  file: File;
  projectId: string;
  userId?: string;
}): Promise<LogUploadResponse> {
  const { file, projectId, userId } = params;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', projectId);
  if (userId) formData.append('userId', userId);
  const response = await api.post<LogUploadResponse>('/csa-plus/log-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

export interface SentinelAnalysisSummary {
  total_events: number;
  total_findings: number;
  total_risk_score: number;
  trust_score: number;
  vendor_severity_distribution: Record<string, number>;
  risk_distribution: Record<string, number>;
}

export interface SentinelAnalysisPlotData {
  vendor_severity_distribution: Record<string, number>;
  risk_distribution: Record<string, number>;
  mitre_distribution: [string, number][];
  cis_distribution: [number | string, number][];
  nist_distribution: [string, number][];
  endpoint_risk: [string, number][];
  thresholds: Record<string, number>;
  labels: {
    mitre_names?: Record<string, string>;
    nist_full_names?: Record<string, string>;
    cis_control_titles?: Record<string, string>;
  };
}

export interface SentinelFinding {
  event_id: string;
  timestamp?: string;
  endpoint?: string;
  event_type?: string;
  severity_vendor?: string;
  mitre?: string[];
  cis_controls?: (number | string)[];
  risk_score: number;
  risk_level: string;
  recommended_action?: string;
}

export interface SentinelAnalysisResult {
  projectId: string;
  result_id?: string;
  log_upload_id?: string;
  log_file_url?: string;
  risk_model?: Record<string, unknown>;
  summary: SentinelAnalysisSummary;
  plot_data: SentinelAnalysisPlotData;
  events_by_severity?: Record<string, SentinelFinding[]>;
  action_required_high_critical?: SentinelFinding[];
  findings?: SentinelFinding[];
}

export async function runSentinelAnalysis(params: {
  projectId: string;
  save_result?: boolean;
}): Promise<SentinelAnalysisResult> {
  const { projectId, save_result = true } = params;
  const response = await api.post<SentinelAnalysisResult>('/csa-plus/sentinel-analysis', {
    projectId,
    save_result
  });
  return response.data;
}

export async function deleteCSAPlusProject(projectId: string, userId?: string): Promise<void> {
  const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const url = `/csa-plus/project/${encodeURIComponent(projectId)}${query}`;
  await api.delete(url);
}

export interface EvaluatedRule {
  setting_name: string;
  actual_value: any;
  expected_value: any;
  operator: string;
  status: 'pass' | 'fail' | 'warning' | 'unchecked';
  category: string;
  explanation: string;
  remediation_step?: string;
  framework_citation?: string;
}

export interface ConfigAnalysisSummary {
  trust_score: number;
  total_config_settings?: number;
  total_rules_evaluated: number;
  compliant_count: number;
  non_compliant_count: number;
  unchecked_count?: number;
  executive_summary?: string;
}

export interface ConfigAnalysisPlotData {
  category_scores: { category: string; score: number }[];
  control_scores: { control_id: string; compliance_percentage: number }[];
  risk_heatmap: { likelihood: number; impact: number; weight: number; issue: string }[];
  compliance_comparison: { framework: string; internal_score: number; benchmark_score: number }[];
}

export interface ConfigAnalysisResult {
  projectId?: string;
  result_id?: string;
  summary_stats: ConfigAnalysisSummary;
  evaluated_rules: EvaluatedRule[];
  plot_data: ConfigAnalysisPlotData;
}

export interface ConfigAnalysisFileEntry {
  upload_id?: string;
  filename: string;
  timestamp?: string;
  result: ConfigAnalysisResult;
}

export interface ConfigAnalysisPerFileResponse {
  per_file_results: Record<string, ConfigAnalysisFileEntry>;
  files: { upload_id: string; filename: string }[];
}

const MAX_SENTINEL_FINDINGS_IN_UI = 400;

function isSentinelStyleSummaryPlot(summary: unknown, plot: unknown): boolean {
  if (!summary || typeof summary !== 'object' || !plot || typeof plot !== 'object') return false;
  const s = summary as Record<string, unknown>;
  return typeof s.trust_score === 'number' && ('total_findings' in s || 'total_events' in s);
}

function isSentinelStyleRoot(o: Record<string, unknown>): boolean {
  return isSentinelStyleSummaryPlot(o.summary, o.plot_data);
}

function mapSentinelFindingToRule(f: Record<string, unknown>, i: number): EvaluatedRule {
  const level = String(f.risk_level ?? 'unknown').toLowerCase();
  let status: EvaluatedRule['status'] = 'pass';
  if (level === 'critical' || level === 'high') status = 'fail';
  else if (level === 'medium') status = 'warning';
  const labels = Array.isArray(f.mongo_rule_labels) ? (f.mongo_rule_labels as unknown[]).join('; ') : '';
  const mitre = Array.isArray(f.mitre) ? (f.mitre as unknown[]).join(', ') : '';
  const cis = Array.isArray(f.cis_controls) ? (f.cis_controls as unknown[]).join(', ') : '';
  return {
    setting_name: String(f.event_id ?? `finding-${i + 1}`),
    actual_value: f.risk_score ?? '—',
    expected_value: 'Acceptable exposure',
    operator: '≤',
    status,
    category: String(f.risk_level ?? 'finding'),
    explanation: String(
      f.recommended_action != null && String(f.recommended_action).trim() !== '' ? f.recommended_action : labels || '—'
    ),
    remediation_step: typeof f.recommended_action === 'string' ? f.recommended_action : undefined,
    framework_citation: [mitre, cis].filter(Boolean).join(' · ') || undefined
  };
}

export function buildConfigResultFromSentinelPayload(raw: Record<string, unknown>): ConfigAnalysisResult {
  const summaryObj = raw.summary as Record<string, unknown>;
  const plotObj = raw.plot_data as Record<string, unknown>;
  const trust = Number(summaryObj.trust_score ?? 0);
  const totalFindings = Number(summaryObj.total_findings ?? summaryObj.total_events ?? 0);
  const rd = (summaryObj.risk_distribution ?? {}) as Record<string, number>;
  const nonCompliant = (Number(rd.critical) || 0) + (Number(rd.high) || 0) + (Number(rd.medium) || 0);
  const compliant = (Number(rd.low) || 0) + (Number(rd.informational) || 0);

  const findingsRaw = [
    ...((Array.isArray(raw.findings) ? raw.findings : []) as Record<string, unknown>[]),
    ...((Array.isArray(raw.action_required_high_critical) ? raw.action_required_high_critical : []) as Record<
      string,
      unknown
    >[])
  ];
  const seen = new Set<string>();
  const findings: Record<string, unknown>[] = [];
  for (const f of findingsRaw) {
    const id = String(f.event_id ?? '');
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    findings.push(f);
  }
  const sampled = findings.slice(0, MAX_SENTINEL_FINDINGS_IN_UI);
  const evaluated_rules: EvaluatedRule[] = sampled.map((f, i) => mapSentinelFindingToRule(f, i));

  const rdSum = Object.values(rd).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalForShare = Math.max(1, totalFindings, rdSum);
  const execParts = [
    `Trust score ${trust}/100 based on ${totalFindings || findings.length} findings.`,
    `Severity mix: critical ${rd.critical ?? 0}, high ${rd.high ?? 0}, medium ${rd.medium ?? 0}, low ${rd.low ?? 0}.`,
    findings.length > sampled.length
      ? `Showing ${sampled.length} of ${findings.length} detailed rows (performance cap).`
      : ''
  ].filter(Boolean);

  const summary_stats: ConfigAnalysisSummary = {
    trust_score: trust,
    total_rules_evaluated: totalFindings || findings.length,
    compliant_count: compliant,
    non_compliant_count: nonCompliant,
    unchecked_count: 0,
    executive_summary: execParts.join(' ')
  };

  const category_scores = Object.entries(rd).map(([k, v]) => ({
    category: k.charAt(0).toUpperCase() + k.slice(1),
    score: Math.min(100, Math.round((Number(v) / totalForShare) * 100))
  }));

  const cisTuples = (Array.isArray(plotObj.cis_distribution) ? plotObj.cis_distribution : []) as [unknown, unknown][];
  const maxCis = Math.max(1, ...cisTuples.map(([, c]) => Number(c) || 0));
  const control_scores = cisTuples.slice(0, 20).map(([cid, count]) => ({
    control_id: String(cid),
    compliance_percentage: Math.max(0, Math.min(100, Math.round(100 - (Number(count) / maxCis) * 100)))
  }));

  const nistTuples = (Array.isArray(plotObj.nist_distribution) ? plotObj.nist_distribution : []) as [
    unknown,
    unknown
  ][];
  const maxN = Math.max(1, ...nistTuples.map(([, c]) => Number(c) || 0));
  const compliance_comparison = nistTuples.slice(0, 8).map(([fn, count]) => ({
    framework: String(fn),
    internal_score: Math.max(0, Math.min(100, Math.round(100 - (Number(count) / maxN) * 100))),
    benchmark_score: 70
  }));

  const plot_data: ConfigAnalysisPlotData = {
    category_scores,
    control_scores,
    risk_heatmap: [],
    compliance_comparison
  };

  return {
    projectId: typeof raw.projectId === 'string' ? raw.projectId : undefined,
    result_id: typeof raw.result_id === 'string' ? raw.result_id : undefined,
    summary_stats,
    evaluated_rules,
    plot_data
  };
}

function sentinelRootToPerFileResponse(o: Record<string, unknown>): ConfigAnalysisPerFileResponse {
  const id = String(o.log_upload_id ?? o.result_id ?? '__run_analysis__');
  const result = buildConfigResultFromSentinelPayload(o);
  const filename =
    typeof o.log_file_url === 'string'
      ? (o.log_file_url.split('/').pop() || 'Log analysis').slice(0, 120)
      : 'Log analysis';
  return {
    per_file_results: {
      [id]: { upload_id: id, filename, result }
    },
    files: [{ upload_id: id, filename }]
  };
}

function normalizeFileEntryShape(entry: unknown): ConfigAnalysisFileEntry | null {
  if (!entry || typeof entry !== 'object') return null;
  const e = entry as Record<string, unknown>;
  if (e.result && typeof e.result === 'object') {
    const r = e.result as Record<string, unknown>;
    if (r.summary_stats != null && typeof r.summary_stats === 'object') {
      return e as unknown as ConfigAnalysisFileEntry;
    }
    if (r.summary && r.plot_data) {
      const result = buildConfigResultFromSentinelPayload(r as Record<string, unknown>);
      return {
        filename: String(e.filename ?? 'Analysis'),
        upload_id: e.upload_id as string | undefined,
        timestamp: e.timestamp as string | undefined,
        result
      };
    }
    if (Array.isArray(r.evaluated_rules) || r.plot_data != null) {
      return e as unknown as ConfigAnalysisFileEntry;
    }
  }
  if (e.summary && e.plot_data && typeof e.summary === 'object' && typeof e.plot_data === 'object') {
    const result = buildConfigResultFromSentinelPayload(e as Record<string, unknown>);
    return {
      filename: String(e.filename ?? 'Analysis'),
      upload_id: e.upload_id as string | undefined,
      timestamp: e.timestamp as string | undefined,
      result
    };
  }
  return null;
}

export function normalizeConfigAnalysisResponse(raw: unknown): ConfigAnalysisPerFileResponse | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;

  if (o.per_file_results && typeof o.per_file_results === 'object' && !Array.isArray(o.per_file_results)) {
    const rawMap = o.per_file_results as Record<string, unknown>;
    const normalizedEntries: Record<string, ConfigAnalysisFileEntry> = {};
    for (const [k, v] of Object.entries(rawMap)) {
      const ne = normalizeFileEntryShape(v);
      if (ne) normalizedEntries[k] = ne;
    }
    const keys = Object.keys(normalizedEntries);
    if (keys.length === 0) return null;

    const filesRaw = o.files;
    const files = Array.isArray(filesRaw)
      ? (filesRaw as { upload_id: string; filename: string }[]).filter(f => f?.upload_id)
      : [];
    const filesOut =
      files.length > 0
        ? files
        : keys.map(k => ({
            upload_id: normalizedEntries[k].upload_id ?? k,
            filename: normalizedEntries[k].filename
          }));

    return { per_file_results: normalizedEntries, files: filesOut };
  }

  if (isSentinelStyleRoot(o)) {
    return sentinelRootToPerFileResponse(o);
  }

  return null;
}

export async function runConfigAnalysis(params: {
  projectId: string;
  save_result?: boolean;
  model_strategy?: 'local' | 'openai';
}): Promise<ConfigAnalysisPerFileResponse> {
  const { projectId, save_result = true, model_strategy = 'openai' } = params;
  const response = await api.post<unknown>('/csa-plus/config-analysis', {
    projectId,
    save_result,
    model_strategy
  });
  const normalized = normalizeConfigAnalysisResponse(response.data);
  if (normalized) return normalized;
  return response.data as ConfigAnalysisPerFileResponse;
}

export async function triggerConfigAnalysisV3(params: {
  projectId: string;
  policyUploadIds: string[];
  configUploadIds: string[];
  forceRemap?: boolean;
}): Promise<{ status: string; project_id: string }> {
  const { projectId, policyUploadIds, configUploadIds, forceRemap = false } = params;
  const response = await api.post(`/csa-plus/${encodeURIComponent(projectId)}/config-analysis/run`, {
    policy_upload_ids: policyUploadIds,
    config_upload_ids: configUploadIds,
    force_remap: forceRemap
  });
  return response.data;
}

function entryTimestampMs(entry: ConfigAnalysisFileEntry | Record<string, unknown> | undefined): number {
  if (!entry || typeof entry !== 'object') return 0;
  const e = entry as Record<string, unknown>;
  const direct = e.timestamp;
  const nested = e.result && typeof e.result === 'object' ? (e.result as Record<string, unknown>).timestamp : undefined;
  const t = direct ?? nested;
  if (t == null || t === '') return 0;
  const ms = new Date(String(t)).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function latestPerFileResultTimestampMs(data: ConfigAnalysisPerFileResponse | null | undefined): number {
  if (!data?.per_file_results) return 0;
  const vals = Object.values(data.per_file_results);
  return vals.length ? Math.max(0, ...vals.map(e => entryTimestampMs(e as ConfigAnalysisFileEntry))) : 0;
}

function entryHasUsableAnalysis(entry: ConfigAnalysisFileEntry | Record<string, unknown> | undefined): boolean {
  if (!entry || typeof entry !== 'object') return false;
  const e = entry as Record<string, unknown>;
  if (e.result && typeof e.result === 'object') {
    const r = e.result as Record<string, unknown>;
    if (r.summary_stats != null && typeof r.summary_stats === 'object') return true;
    if (Array.isArray(r.evaluated_rules) && r.evaluated_rules.length > 0) return true;
    if (r.plot_data != null) return true;
  }
  if (e.summary_stats != null && typeof e.summary_stats === 'object') return true;
  if (Array.isArray(e.evaluated_rules) && e.evaluated_rules.length > 0) return true;
  if (e.plot_data != null) return true;
  return false;
}

function responseHasCompletedAnalysis(data: ConfigAnalysisPerFileResponse | null | undefined): boolean {
  const map = data?.per_file_results;
  if (!map || typeof map !== 'object') return false;
  return Object.values(map).some(e => entryHasUsableAnalysis(e as ConfigAnalysisFileEntry));
}

function perFileResultsDigest(data: ConfigAnalysisPerFileResponse | null | undefined): string {
  if (!data?.per_file_results) return '';
  const keys = Object.keys(data.per_file_results).sort();
  try {
    return JSON.stringify(keys.map(k => [k, data.per_file_results[k]]));
  } catch {
    return '';
  }
}

function pollHasFreshAnalysis(
  data: ConfigAnalysisPerFileResponse | null | undefined,
  triggerTime: number,
  baselineDigest: string
): boolean {
  if (!data || !responseHasCompletedAnalysis(data)) return false;
  if (latestPerFileResultTimestampMs(data) > triggerTime) return true;
  return perFileResultsDigest(data) !== baselineDigest;
}

/**
 * Trigger a V3 analysis run and poll until results are available.
 * Returns the per-file response once the background task completes.
 * Throws if no result appears within the timeout window.
 */
export async function runConfigAnalysisV3(params: {
  projectId: string;
  policyUploadIds: string[];
  configUploadIds: string[];
  forceRemap?: boolean;
  pollIntervalMs?: number;
  timeoutMs?: number;
}): Promise<ConfigAnalysisPerFileResponse> {
  const {
    projectId,
    policyUploadIds,
    configUploadIds,
    forceRemap = false,
    pollIntervalMs = 30000,
    timeoutMs = 300000 // 5 min
  } = params;

  const baseline = await fetchLatestConfigAnalysis(projectId);
  const baselineDigest = perFileResultsDigest(baseline);

  const triggerTime = Date.now();
  await triggerConfigAnalysisV3({ projectId, policyUploadIds, configUploadIds, forceRemap });

  await new Promise(r => setTimeout(r, 3000));
  const earlyResult = await fetchLatestConfigAnalysis(projectId);
  if (earlyResult && pollHasFreshAnalysis(earlyResult, triggerTime, baselineDigest)) return earlyResult;

  const deadline = triggerTime + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, pollIntervalMs));
    const result = await fetchLatestConfigAnalysis(projectId);
    if (result && pollHasFreshAnalysis(result, triggerTime, baselineDigest)) return result;
  }
  throw new Error('Config analysis timed out waiting for results.');
}

export async function fetchLatestConfigAnalysis(projectId: string): Promise<ConfigAnalysisPerFileResponse | null> {
  try {
    const response = await api.get<unknown>(`/csa-plus/${encodeURIComponent(projectId)}/config-analysis/latest`);
    const normalized = normalizeConfigAnalysisResponse(response.data);
    if (normalized) return normalized;
    const raw = response.data;
    if (
      raw &&
      typeof raw === 'object' &&
      'per_file_results' in raw &&
      (raw as ConfigAnalysisPerFileResponse).per_file_results
    ) {
      return raw as ConfigAnalysisPerFileResponse;
    }
    return null;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}
